import { useState } from "react";
import {
  Stack,
  Group,
  Badge,
  Button,
  Text,
  Code,
  Paper,
  Title,
  Divider,
  ScrollArea,
  Center,
  Loader,
  Tooltip,
  Anchor,
  Collapse,
  Box,
  Alert,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconReload,
  IconCircleCheck,
  IconX,
  IconAlertTriangle,
  IconCode,
  IconUser,
  IconKey,
  IconTrash,
} from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { apWorkbenchApi } from "../../../api/apWorkbench.api";
import { apiError } from "../../../api/client";
import { useAuth } from "../../../auth/AuthContext";
import { fmtDate, fmtMoney } from "./helpers";
import { ImagesPane } from "./ImagesPane";
import { LinesTable } from "./LinesTable";

/**
 * Full-width AP invoice review.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Back · invoice header · action bar                          │
 *   ├──────────────────────────┬──────────────────────────────────┤
 *   │ ImagesPane (zoom/pan)    │ ResolutionPane                   │
 *   │                          │   ├ header fields                │
 *   │                          │   └ LinesTable                   │
 *   └──────────────────────────┴──────────────────────────────────┘
 *
 * Owns ALL mutations for the invoice so React-Query cache updates are
 * applied at one place and propagated to every consumer immediately.
 */
export function InvoiceReviewPage({ invoiceId, onBack }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const perms = user?.modulePermissions?.["ap-workbench"] || {};
  const canEdit = user?.isAdmin || !!perms.edit;
  const canMarkReady = user?.isAdmin || !!perms.mark_ready;

  const invoiceQ = useQuery({
    queryKey: ["ap-workbench", "invoice", invoiceId],
    queryFn: () => apWorkbenchApi.getInvoice(invoiceId),
    enabled: !!invoiceId,
  });

  const imagesQ = useQuery({
    queryKey: ["ap-workbench", "images", invoiceId],
    queryFn: () => apWorkbenchApi.getImages(invoiceId),
    enabled: !!invoiceId,
    staleTime: 5 * 60 * 1000,
  });

  // Centralised cache update applied after every successful write.
  const applyInvoice = (inv) => {
    qc.setQueryData(["ap-workbench", "invoice", invoiceId], inv);
    qc.invalidateQueries({ queryKey: ["ap-workbench", "list"] });
    qc.invalidateQueries({ queryKey: ["ap-workbench", "counts"] });
  };
  const showError = (e) =>
    notifications.show({ color: "red", message: apiError(e) });

  const refreshMut = useMutation({
    mutationFn: () => apWorkbenchApi.refreshDenorm(invoiceId),
    onSuccess: (inv) => {
      notifications.show({ color: "green", message: "SAP snapshot refreshed" });
      applyInvoice(inv);
      qc.invalidateQueries({ queryKey: ["ap-workbench", "images", invoiceId] });
    },
    onError: showError,
  });

  const markReadyMut = useMutation({
    mutationFn: () => apWorkbenchApi.markReady(invoiceId),
    onSuccess: (inv) => {
      notifications.show({ color: "green", message: "Invoice marked ready" });
      applyInvoice(inv);
    },
    onError: showError,
  });

  const unmarkMut = useMutation({
    mutationFn: () => apWorkbenchApi.unmarkReady(invoiceId),
    onSuccess: (inv) => {
      notifications.show({ color: "green", message: "Ready flag cleared" });
      applyInvoice(inv);
    },
    onError: showError,
  });

  const resolveMut = useMutation({
    mutationFn: ({ lineId, body }) =>
      apWorkbenchApi.resolveLine(invoiceId, lineId, body),
    onSuccess: (inv) => {
      notifications.show({ color: "green", message: "Line resolved" });
      applyInvoice(inv);
    },
    onError: showError,
  });

  const unresolveMut = useMutation({
    mutationFn: (lineId) => apWorkbenchApi.unresolveLine(invoiceId, lineId),
    onSuccess: (inv) => {
      notifications.show({ color: "green", message: "Line un-resolved" });
      applyInvoice(inv);
    },
    onError: showError,
  });

  const deleteMut = useMutation({
    mutationFn: () => apWorkbenchApi.deleteInvoice(invoiceId),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Invoice deleted" });
      qc.removeQueries({ queryKey: ["ap-workbench", "invoice", invoiceId] });
      qc.removeQueries({ queryKey: ["ap-workbench", "images", invoiceId] });
      qc.invalidateQueries({ queryKey: ["ap-workbench", "list"] });
      qc.invalidateQueries({ queryKey: ["ap-workbench", "counts"] });
      onBack?.();
    },
    onError: showError,
  });

  const confirmDelete = () => {
    if (!invoice) return;
    const ok = window.confirm(
      `Permanently delete invoice ${invoice.invoiceNumber} (GR ${invoice.grDocEntry})?\n\nThis cannot be undone.`
    );
    if (ok) deleteMut.mutate();
  };

  const [rawOpen, rawHandlers] = useDisclosure(false);

  const invoice = invoiceQ.data;

  if (invoiceQ.isLoading || !invoice) {
    return (
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={14} />}
            onClick={onBack}
          >
            Back to invoices
          </Button>
        </Group>
        <Center py="xl">
          <Loader />
        </Center>
      </Stack>
    );
  }

  const unresolvedCount =
    invoice.lines?.filter((l) => l.kind === "mismatched" && !l.isResolved)
      ?.length || 0;
  const locked = invoice.pushedToSap;
  const lockedForEdit = locked || invoice.readyForSap;

  // Pending-line flags for spinner display in the table.
  const resolvingLineId =
    resolveMut.isPending ? resolveMut.variables?.lineId : null;
  const unresolvingLineId = unresolveMut.isPending ? unresolveMut.variables : null;

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 120px)",
        minHeight: 600,
        gap: 12,
      }}
      data-ap-review-root
    >
      <Stack gap="xs" style={{ flex: "0 0 auto" }}>
        <Group justify="space-between" wrap="wrap" align="flex-start">
          <Group gap="md" wrap="nowrap">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={14} />}
              onClick={onBack}
            >
              Back
            </Button>
            <div>
              <Group gap="xs">
                <Title order={3}>{invoice.invoiceNumber}</Title>
                <Code>GR {invoice.grDocEntry}</Code>
              </Group>
              <Group gap={6} mt={2}>
                <Text size="sm" c="dimmed">
                  {invoice.grHeaderSnapshot?.cardName || "—"}
                  {invoice.grHeaderSnapshot?.cardCode
                    ? ` · ${invoice.grHeaderSnapshot.cardCode}`
                    : ""}
                </Text>
                <Text size="sm" c="dimmed">
                  · invoice date {fmtDate(invoice.invoiceDate)}
                </Text>
              </Group>
            </div>
          </Group>

          <Group gap="xs" wrap="wrap" justify="flex-end">
            <StatusBadges invoice={invoice} />
          </Group>
        </Group>

        <ActionBar
          invoice={invoice}
          canEdit={canEdit}
          canMarkReady={canMarkReady}
          isAdmin={!!user?.isAdmin}
          locked={locked}
          unresolvedCount={unresolvedCount}
          refreshPending={refreshMut.isPending}
          markReadyPending={markReadyMut.isPending}
          unmarkPending={unmarkMut.isPending}
          deletePending={deleteMut.isPending}
          onRefresh={() => refreshMut.mutate()}
          onMarkReady={() => markReadyMut.mutate()}
          onUnmark={() => unmarkMut.mutate()}
          onDelete={confirmDelete}
          onToggleRaw={rawHandlers.toggle}
          rawOpen={rawOpen}
        />

        {invoice.denormError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} variant="light">
            <Text size="xs" component="div" style={{ whiteSpace: "pre-wrap" }}>
              {invoice.denormError}
            </Text>
          </Alert>
        )}

        {locked && (
          <Alert color="blue" variant="light" icon={<IconCircleCheck size={16} />}>
            Pushed to SAP
            {invoice.sapPushedAt
              ? ` on ${new Date(invoice.sapPushedAt).toLocaleString()}`
              : ""}
            {invoice.sapInvoiceDocEntry
              ? ` (DocEntry ${invoice.sapInvoiceDocEntry})`
              : ""}
            . Read-only.
          </Alert>
        )}
      </Stack>

      {/*
        Two-column split. The grid itself is the flex child that fills the
        remaining vertical space; both columns are 100 % of that height.
        The right column is its own scroll container so scrolling the lines
        list never scrolls the image out of view.
      */}
      <Box
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: "minmax(380px, 0.78fr) minmax(0, 1fr)",
          gap: 16,
        }}
        data-ap-review-grid
      >
        <Box style={{ minWidth: 0, minHeight: 0, height: "100%" }}>
          <ImagesPane images={imagesQ.data || []} loading={imagesQ.isLoading} />
        </Box>

        <Box
          style={{
            minWidth: 0,
            minHeight: 0,
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: 4,
          }}
        >
          <Stack gap="md">
            <HeaderCard invoice={invoice} />
            <LinesTable
              invoice={invoice}
              locked={lockedForEdit || !canEdit}
              onResolve={(lineId, body) => resolveMut.mutate({ lineId, body })}
              onUnresolve={(lineId) => unresolveMut.mutate(lineId)}
              resolvingLineId={resolvingLineId}
              unresolvingLineId={unresolvingLineId}
            />
            <Collapse in={rawOpen}>
              <Paper p="sm" withBorder>
                <Title order={5} mb="xs">
                  Original payload
                </Title>
                <Divider mb="xs" />
                <ScrollArea h={260}>
                  <Code block style={{ whiteSpace: "pre", fontSize: 11 }}>
                    {JSON.stringify(invoice.rawPayload, null, 2)}
                  </Code>
                </ScrollArea>
              </Paper>
            </Collapse>
          </Stack>
        </Box>
      </Box>

      <style>{`
        @media (max-width: 980px) {
          [data-ap-review-root] {
            height: auto !important;
          }
          [data-ap-review-grid] {
            grid-template-columns: 1fr !important;
          }
          [data-ap-review-grid] > :first-child {
            height: 60vh !important;
          }
          [data-ap-review-grid] > :last-child {
            height: auto !important;
            overflow: visible !important;
          }
        }
      `}</style>
    </Box>
  );
}

function StatusBadges({ invoice }) {
  return (
    <>
      <Badge
        color={invoice.status === "matched" ? "green" : "orange"}
        variant="light"
      >
        {invoice.status}
      </Badge>
      <Badge
        variant="light"
        color={invoice.kind === "full" ? "blue" : "orange"}
      >
        {invoice.kind}
      </Badge>
      {invoice.pushedToSap ? (
        <Badge color="blue" variant="filled">
          Posted to SAP
        </Badge>
      ) : invoice.readyForSap ? (
        <Badge color="violet" variant="light">
          Ready for SAP
        </Badge>
      ) : (
        <Badge color="gray" variant="light">
          Draft
        </Badge>
      )}
      <Badge
        color={
          invoice.denormStatus === "ok"
            ? "teal"
            : invoice.denormStatus === "pending"
            ? "yellow"
            : "red"
        }
        variant="light"
        size="sm"
      >
        denorm: {invoice.denormStatus}
      </Badge>
    </>
  );
}

function ActionBar({
  invoice,
  canEdit,
  canMarkReady,
  isAdmin,
  locked,
  unresolvedCount,
  refreshPending,
  markReadyPending,
  unmarkPending,
  deletePending,
  onRefresh,
  onMarkReady,
  onUnmark,
  onDelete,
  onToggleRaw,
  rawOpen,
}) {
  return (
    <Paper p="xs" withBorder>
      <Group gap="xs" wrap="wrap" justify="space-between">
        <Group gap="xs" wrap="wrap">
          {canEdit && (
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconReload size={14} />}
              onClick={onRefresh}
              loading={refreshPending}
              disabled={locked}
            >
              Refresh SAP snapshot
            </Button>
          )}
          {canMarkReady && !invoice.readyForSap && !invoice.pushedToSap && (
            <Tooltip
              disabled={unresolvedCount === 0}
              label={`Resolve ${unresolvedCount} mismatched line(s) first`}
            >
              <Button
                size="xs"
                color="violet"
                leftSection={<IconCircleCheck size={14} />}
                onClick={onMarkReady}
                loading={markReadyPending}
                disabled={unresolvedCount > 0}
              >
                Mark ready for SAP
              </Button>
            </Tooltip>
          )}
          {canMarkReady && invoice.readyForSap && !invoice.pushedToSap && (
            <Button
              size="xs"
              variant="subtle"
              color="orange"
              leftSection={<IconX size={14} />}
              onClick={onUnmark}
              loading={unmarkPending}
            >
              Unmark ready
            </Button>
          )}
          <Button
            size="xs"
            variant="subtle"
            leftSection={<IconCode size={14} />}
            onClick={onToggleRaw}
          >
            {rawOpen ? "Hide raw payload" : "Show raw payload"}
          </Button>
          {isAdmin && !invoice.pushedToSap && (
            <Tooltip label="Admin / testing — permanently delete this invoice">
              <Button
                size="xs"
                variant="light"
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={onDelete}
                loading={deletePending}
              >
                Delete
              </Button>
            </Tooltip>
          )}
        </Group>
        <Group gap={4}>
          {invoice.readyForSap && (
            <Anchor size="xs" c="violet" style={{ fontStyle: "italic" }}>
              Ready by {invoice.readyForSapBy?.fullName || "—"}
              {invoice.readyForSapAt
                ? ` · ${new Date(invoice.readyForSapAt).toLocaleString()}`
                : ""}
            </Anchor>
          )}
        </Group>
      </Group>
    </Paper>
  );
}

function HeaderCard({ invoice }) {
  const snap = invoice.grHeaderSnapshot || {};
  const cells = [
    ["GR DocEntry", <Code>{invoice.grDocEntry}</Code>],
    ["GR DocNum", snap.docNum || "—"],
    ["PO DocNum", snap.poDocNum || "—"],
    [
      "Supplier",
      snap.cardName ? `${snap.cardName} (${snap.cardCode})` : "—",
    ],
    ["Branch", snap.branch || "—"],
    ["Invoice date", fmtDate(invoice.invoiceDate)],
    ["GR doc date", fmtDate(snap.docDate)],
    ["GR total", fmtMoney(snap.docTotal)],
    ["Received at", new Date(invoice.receivedAt).toLocaleString()],
    [
      "Source",
      invoice.receivedByApiKey ? (
        <Group gap={4}>
          <IconKey size={12} />
          <Text size="sm">{invoice.receivedByApiKey.name}</Text>
        </Group>
      ) : invoice.receivedByUser ? (
        <Group gap={4}>
          <IconUser size={12} />
          <Text size="sm">{invoice.receivedByUser.fullName}</Text>
        </Group>
      ) : (
        "—"
      ),
    ],
  ];
  return (
    <Paper withBorder p="sm">
      <Group gap="xs" justify="space-between" mb="xs">
        <Title order={5}>Header</Title>
      </Group>
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          rowGap: 6,
          columnGap: 16,
        }}
      >
        {cells.map(([k, v]) => (
          <Group key={k} gap="md" wrap="nowrap">
            <Text size="xs" c="dimmed" w={110}>
              {k}
            </Text>
            <Text size="sm" component="div" style={{ minWidth: 0 }}>
              {v}
            </Text>
          </Group>
        ))}
      </Box>
    </Paper>
  );
}
